import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { PrimaryButton, SecondaryButton } from "../../UI";
import PrimaryModal from '../../UI/Modal/PrimaryModal';
import Select from '../../UI/Select';
import Input, { Textarea } from '../../UI/Input';
import Toggle from '../../UI/Toggle';
import { useEPMContext } from '../../../contexts/EPMContext';
import { EPM_LIMITS } from '../validation/epmRules';
import { locationFieldSchema } from '../validation/epmSchemas';
import EpmCharCounter from '../EpmCharCounter';

const LocationFieldModal = ({ location, totalFields, onClose, onSaved }) => {
    const { createTestLocation, updateTestLocation } = useEPMContext();
    const isEditing = Boolean(location?._id);
    const initialValues = useMemo(() => ({
        label: location?.label ?? '',
        key: location?.key ?? '',
        field_type: location?.field_type ?? 'text_box',
        field_options: (location?.field_options ?? []).join(', '),
        validation_pattern: location?.validation_pattern ?? '',
        order: location?.order ?? totalFields + 1,
        conflict_role: location?.conflict_role ?? 'None',
        required: location?.required ?? true,
    }), [location, totalFields]);

    const [form, setForm] = useState(initialValues);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);

    const setValue = (field, value) => {
        setForm((current) => ({ ...current, [field]: value }));
        setErrors((current) => ({ ...current, [field]: '' }));
    };

    const validate = () => {
        const nextErrors = {};
        try {
            locationFieldSchema.validateSync(form, { abortEarly: false });
        } catch (err) {
            if (err.inner && err.inner.length > 0) {
                err.inner.forEach((validationErr) => {
                    nextErrors[validationErr.path] = validationErr.message;
                });
            } else {
                nextErrors[err.path || 'form'] = err.message;
            }
        }

        if (form.field_type === 'dropdown') {
            const options = form.field_options.split(',').map((o) => o.trim()).filter(Boolean);
            if (!options.length) {
                nextErrors.field_options = 'Add at least one dropdown option.';
            } else {
                const overLong = options.find((o) => o.length > EPM_LIMITS.LOCATION_OPTION_MAX);
                if (overLong) {
                    nextErrors.field_options = `Option "${overLong}" exceeds maximum ${EPM_LIMITS.LOCATION_OPTION_MAX} characters.`;
                }
                const lower = options.map((o) => o.toLowerCase());
                const uniqueSet = new Set(lower);
                if (uniqueSet.size < lower.length) {
                    nextErrors.field_options = 'Duplicate dropdown options are not allowed.';
                }
            }
        }

        const maxOrder = isEditing ? totalFields : totalFields + 1;
        if (!Number.isInteger(Number(form.order)) || Number(form.order) < 1 || Number(form.order) > maxOrder) {
            nextErrors.order = `Order must be between 1 and ${maxOrder}.`;
        }

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!validate()) return;

        const payload = {
            label: form.label.trim(),
            key: form.key.trim().toLowerCase(),
            field_type: form.field_type,
            field_options: form.field_type === 'dropdown'
                ? form.field_options.split(',').map((option) => option.trim()).filter(Boolean)
                : [],
            validation_pattern: form.field_type === 'text_box' ? form.validation_pattern.trim() : '',
            order: Number(form.order),
            conflict_role: form.conflict_role,
            required: Boolean(form.required),
        };

        setSaving(true);
        try {
            if (isEditing) {
                await updateTestLocation(location._id, payload);
            } else {
                await createTestLocation(payload);
            }
            toast.success(`Location field ${isEditing ? 'updated' : 'created'} successfully.`);
            onSaved();
        } catch (error) {
            const message = error?.message || `Unable to ${isEditing ? 'update' : 'create'} the location field.`;
            toast.error(message);
            setErrors((current) => ({ ...current, form: message }));
        } finally {
            setSaving(false);
        }
    };

    return (
        <PrimaryModal title={isEditing ? 'Edit Location Field' : 'Add New Location Field'} hideModal={onClose} loading={saving} widthClass="max-w-xl">
            <form className="epm-location-modal" onSubmit={handleSubmit}>
                <div className="epm-location-modal__grid">
                    <div>
                        <Input
                            id="label"
                            label="Label"
                            autoFocus
                            maxLength={EPM_LIMITS.LOCATION_LABEL_MAX}
                            showCharCount
                            value={form.label}
                            onChange={(event) => setValue('label', event.target.value)}
                            placeholder="e.g. Funnel step"
                            error={errors.label}
                        />
                    </div>

                    <div>
                        <Input
                            id="key"
                            label="Key"
                            maxLength={EPM_LIMITS.LOCATION_KEY_MAX}
                            showCharCount
                            value={form.key}
                            onChange={(event) => setValue('key', event.target.value)}
                            placeholder="e.g. funnel_step"
                            error={errors.key}
                        />
                    </div>

                    <label>
                        <span>Field type</span>
                        <Select
                            id="field_type"
                            name="field_type"
                            value={form.field_type}
                            onChange={(event) => setValue('field_type', event.target.value)}
                            options={[
                                { value: 'text_box', label: 'Text box' },
                                { value: 'dropdown', label: 'Dropdown' },
                            ]}
                        />
                    </label>

                    <div>
                        <Input
                            id="order"
                            type="number"
                            label="Order"
                            min="1"
                            max={isEditing ? totalFields : totalFields + 1}
                            value={form.order}
                            onChange={(event) => setValue('order', event.target.value)}
                            error={errors.order}
                        />
                    </div>

                    <label>
                        <span>Conflict role</span>
                        <Select
                            id="conflict_role"
                            name="conflict_role"
                            value={form.conflict_role}
                            onChange={(event) => setValue('conflict_role', event.target.value)}
                            options={[
                                { value: 'None', label: 'None' },
                                { value: 'Hard', label: 'Hard' },
                                { value: 'Soft', label: 'Soft' },
                            ]}
                        />
                    </label>

                    {form.field_type === 'text_box' && (
                        <div>
                            <Input
                                id="validation_pattern"
                                label="Validation pattern (optional regex)"
                                helperText="Optional regex applied when entering test location."
                                value={form.validation_pattern}
                                onChange={(event) => setValue('validation_pattern', event.target.value)}
                                placeholder="e.g. ^/loja/telemoveis/.*$"
                                error={errors.validation_pattern}
                            />
                        </div>
                    )}

                    {form.field_type === 'dropdown' && (
                        <div className="epm-location-modal__full">
                            <Textarea
                                id="field_options"
                                label="Dropdown options"
                                rows={3}
                                helperText={`Separate options with commas (max ${EPM_LIMITS.LOCATION_OPTION_MAX} chars each).`}
                                value={form.field_options}
                                onChange={(event) => setValue('field_options', event.target.value)}
                                placeholder="Option one, Option two, Option three"
                                error={errors.field_options}
                            />
                        </div>
                    )}
                    
                    <div className="epm-location-modal__full flex items-center gap-2.5 pt-1">
                        <Toggle
                            id="required"
                            checked={form.required}
                            onChange={(event) => setValue('required', event.target.checked)}
                            size="sm"
                        />
                        <label htmlFor="required" className="text-xs font-medium text-stride-heading cursor-pointer select-none">
                            Required field
                        </label>
                    </div>
                </div>

                {errors.form && <p className="epm-location-modal__form-error" role="alert">{errors.form}</p>}

                <div className="flex justify-end gap-2.5 mt-6">
                    <SecondaryButton type="button" size="sm" disable={saving} handleClick={onClose}>
                        Cancel
                    </SecondaryButton>
                    <PrimaryButton type="submit" size="sm" loading={saving}>
                        Save
                    </PrimaryButton>
                </div>
            </form>
        </PrimaryModal>
    );
};

export default LocationFieldModal;
